import { useEffect, useMemo, useRef, useState } from 'react';
import { Printer } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useT } from '@/i18n/LanguageContext';
import { engApi } from '@/pages/engineering/engApi';
import { LabelTemplate } from '@/components/labels/LabelTemplate';
import { printLabelNodes } from '@/components/labels/labelPrint';
import { code128Feasibility, planLabel } from '@/components/labels/labelLayout';
import {
  DEFAULT_LABEL_SIZE, DK_LABELS, LABEL_STYLE, buildEngAssetLabel, buildEngEquipmentLabel,
  buildEquipmentLabel, buildItemLabel, buildLocationLabel, buildLotLabel, buildProductTagLabel,
  sizeById,
} from '@/components/labels/labelSizes';

// 見本データ（Code128系）— 実データが不要な種別はここから。QR系は実レコードを引く
// （ペイロードが実際に解決するURLでなければスキャン試験の意味がない）。
const SAMPLES = () => [
  { kind: '品目 (item)', real: false, data: buildItemLabel({ name: 'サンプル品目 ABC-123', sku: 'ITM-0005', barcode: 'ITM-0005' }, 'センスビー製造部') },
  { kind: 'ロット (lot)', real: false, data: buildLotLabel({ name: 'サンプル品目', uom: '個' }, { lot_no: 'LOT-20260101-001', quantity: 50, expiry_date: '2026-12-31' }, 'A-01', 'センスビー製造部') },
  { kind: 'ロケーション (location)', real: false, data: buildLocationLabel({ code: 'A-01', name: '資材棚A', warehouse: '第1倉庫' }, 'センスビー製造部') },
  { kind: '設備・工具（製造） (equipment)', real: false, data: buildEquipmentLabel({ asset_no: 'EQ-001', name: 'リフロー炉', kind_label: '設備', location_name: '製造ライン1' }, 'センスビー製造部') },
  { kind: '製品タグ (product_tag)', real: false, data: buildProductTagLabel({ order_no: 'ORD-1001', due_date: '2026-07-15', supplier: { name: 'サンプル商事' } }, { name: '完成品A', part_no: 'PRD-0001', qty: 100, uom: '個' }) },
];

/**
 * ラベル印字テストシート — 出力はロール印刷のみ。
 * A4出力は作らない: 普通紙には感熱のエッジにじみが無いため、QRのモジュールも
 * Code128の細バーも実際より良く見え、偽の合格しか出せない。
 * 追跡は「印刷順の通し番号 ↔ ペイロード」の対応表（画面）で行う。23mmラベルに
 * URLは入らないので、ラベル自体には載せない。
 * 実寸確認は DK-11221 に1枚印刷してノギスで測る（ドライバの倍率も含めて検証できる）。
 */
export default function LabelTestSheet() {
  const { t } = useT();
  const [size, setSize] = useState(DEFAULT_LABEL_SIZE);
  const [items, setItems] = useState(SAMPLES());
  const rollRef = useRef(null);
  const sz = useMemo(() => sizeById(size), [size]);

  useEffect(() => {
    Promise.all([
      engApi.assets({ limit: 1 }).catch(() => null),
      engApi.equipment({ limit: 1 }).catch(() => null),
    ]).then(([assets, equipment]) => {
      const a = (assets?.items || assets || [])[0];
      const e = (equipment?.items || equipment || [])[0];
      const extra = [];
      if (a) extra.push({ kind: '技術資産 (eng-asset) · 実レコード', real: true, data: buildEngAssetLabel(a) });
      if (e) extra.push({ kind: '設備台帳 (eng-equipment) · 実レコード', real: true, data: buildEngEquipmentLabel(e) });
      if (extra.length) setItems([...extra, ...SAMPLES()]);
    });
  }, []);

  // このロールで印刷できるものだけをロールへ送る。テストシートも印刷物なので、
  // 下限未満のバーコードを「見本だから」と刷らせない（見えない不良を作らない）。
  const rows = items.map((it) => {
    const format = it.data.format || 'code128';
    const feas = format === 'code128' ? code128Feasibility({ data: it.data, w: sz.w, h: sz.h }) : null;
    return { ...it, format, feas, printable: !feas?.tooNarrow, plan: planLabel({ data: it.data, w: sz.w, h: sz.h, format }) };
  });
  const printableRows = rows.filter((r) => r.printable);

  const dieCut = DK_LABELS.filter((s) => s.group === 'die');
  const cont = DK_LABELS.filter((s) => s.group === 'cont');

  return (
    <div className="max-w-4xl space-y-5">
      <style>{LABEL_STYLE}</style>
      <div>
        <h1 className="text-xl font-bold text-zinc-900">ラベル印字テストシート</h1>
        <p className="text-sm text-zinc-500 mt-1">
          全種別を本番と同じ自動レイアウトで QL-800 に印刷します（下の順番どおりに出力）。
          実寸は DK-11221 に1枚印刷してノギスで測ってください — ドライバの倍率も含めて検証できます。
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-64">
          <Label className="text-xs font-semibold text-zinc-600">{t('mfg.label.size')}</Label>
          <Select value={size} onValueChange={setSize}>
            <SelectTrigger className="h-10 mt-1" data-testid="test-sheet-size-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>{t('mfg.label.dieCut')}</SelectLabel>
                {dieCut.map((s) => <SelectItem key={s.id} value={s.id}>{t(s.nameKey)}</SelectItem>)}
              </SelectGroup>
              <SelectGroup>
                <SelectLabel>{t('mfg.label.continuous')}</SelectLabel>
                {cont.map((s) => <SelectItem key={s.id} value={s.id}>{t(s.nameKey)}</SelectItem>)}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
        <Button className="bg-orange-500 hover:bg-orange-600" data-testid="test-sheet-print-roll"
          onClick={() => { if (!printLabelNodes(rollRef.current, sz, 'ラベル印字テスト')) toast.error(t('mfg.label.popupBlocked')); }}>
          <Printer size={15} className="mr-1.5" /> QL-800 ロールに印刷（{sz.w}{sz.h ? `×${sz.h}` : ''}mm · {printableRows.length}枚）
        </Button>
      </div>

      {/* 画面ビュー = 通し番号 ↔ ペイロードの対応表。スキャン失敗をURL単位で追える。 */}
      <div className="ql-preview rounded-lg border border-zinc-200 bg-white p-5 space-y-5" data-testid="test-sheet">
        {rows.map((it, i) => {
          const { format, plan } = it;
          return (
            <div className="flex flex-wrap items-start gap-4" key={it.kind} data-testid={`test-sheet-item-${i + 1}`}>
              <div className="w-8 shrink-0 text-lg font-bold text-zinc-400 tabular-nums">{i + 1}</div>
              <LabelTemplate data={it.data} w={sz.w} h={sz.h} format={format} />
              <div className="min-w-0 flex-1 text-xs">
                <div className="font-semibold text-zinc-800">
                  {it.kind}{it.real ? '' : '（サンプルデータ）'}
                </div>
                <div className="text-zinc-500 mt-0.5">
                  {format === 'qr' ? 'QR' : 'Code128'}
                  {plan ? ` · コード ${plan.codeMm.toFixed(1)}mm` : ' · 従来レイアウト'}
                  {plan?.moduleMm ? ` · ${plan.moduleMm.toFixed(3)}mm/モジュール` : ''}
                  {plan?.tooLong ? ' · ⚠️ このロールにはデータが長すぎます' : ''}
                  {plan?.dropped?.length ? ` · 未印字: ${plan.dropped.join('・')}` : ''}
                </div>
                {!it.printable && (
                  <div className="text-rose-600 font-semibold mt-0.5" data-testid={`test-sheet-excluded-${i + 1}`}>
                    ⚠️ このロールでは細バーが {it.feas.narrowMm.toFixed(3)}mm（下限 {it.feas.minNarrowMm}mm）のため印刷対象から除外
                  </div>
                )}
                <div className="font-mono text-[11px] text-zinc-600 break-all mt-1" data-testid={`test-sheet-payload-${i + 1}`}>
                  {it.data.barcode}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ロール印刷用 — 印刷可能なものだけを同じ LabelTemplate で出す隠しコンテナ。
          印刷経路（labelPrint.printLabelNodes）はダイアログと共通。 */}
      <div ref={rollRef} className="ql-preview hidden">
        {printableRows.map((it) => (
          <LabelTemplate key={`roll-${it.kind}`} data={it.data} w={sz.w} h={sz.h} format={it.format} />
        ))}
      </div>
    </div>
  );
}
